/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { EnhancedObjective } from '../types/enhancedObjectives';

export const ENHANCED_OBJECTIVES: EnhancedObjective[] = [
  // Infrastructure Objectives
  {
    id: 'inf_core_hub',
    title: 'Establish Banking Hub',
    description: 'Build your central banking operations hub',
    detailedDescription: 'The Banking Hub is the heart of your neobank operations. This core facility provides the foundation for all financial services and compliance operations. Establishing a secure and efficient hub is critical for long-term success.',
    categoryId: 'infrastructure',
    subcategoryId: 'core_systems',
    difficulty: 'trivial',
    importance: 'critical',
    status: 'available',
    progress: 0,
    maxValue: 1,
    criteria: {
      type: 'count',
      value: 'Core',
      operator: '>='
    },
    prerequisites: [],
    rewards: [
      {
        type: 'money',
        value: 500,
        description: 'Initial operational funding'
      },
      {
        type: 'building_unlock',
        value: 'Infrastructure',
        description: 'Unlocks basic infrastructure buildings'
      }
    ],
    isRepeatable: false,
    hints: [
      'The Hub should be placed centrally for optimal efficiency',
      'Ensure space for future expansion around the Hub',
      'Consider compliance requirements when placing'
    ],
    successMetrics: {
      primary: 'Build 1 Banking Hub',
      secondary: ['Maintain positive budget', 'Keep compliance above 50%'],
      tertiary: ['Minimize construction time']
    },
    dependencies: [],
    unlocks: ['inf_security_basics', 'inf_compliance_foundation'],
    tags: ['tutorial', 'infrastructure', 'essential'],
    isHidden: false,
    isMandatory: true
  },

  {
    id: 'inf_connectivity_basic',
    title: 'Establish Network Connectivity',
    description: 'Connect your banking hub to external networks',
    detailedDescription: 'Network connectivity enables your neobank to communicate with financial networks, payment processors, and regulatory systems. This is essential for processing transactions and maintaining real-time compliance.',
    categoryId: 'infrastructure',
    subcategoryId: 'core_systems',
    difficulty: 'easy',
    importance: 'high',
    status: 'locked',
    progress: 0,
    maxValue: 1,
    criteria: {
      type: 'count',
      value: 'Internet',
      operator: '>='
    },
    prerequisites: [
      {
        type: 'objective',
        id: 'inf_core_hub',
        description: 'Complete: Establish Banking Hub'
      }
    ],
    rewards: [
      {
        type: 'money',
        value: 200,
        description: 'Network setup bonus'
      },
      {
        type: 'research_points',
        value: 50,
        description: 'Research points for network technologies'
      }
    ],
    isRepeatable: false,
    hints: [
      'Network connectivity enables real-time transactions',
      'Essential for future product launches',
      'May require additional security measures'
    ],
    successMetrics: {
      primary: 'Build 1 Internet Connection',
      secondary: ['Connect to Banking Hub', 'Establish secure protocols']
    },
    dependencies: ['inf_core_hub'],
    unlocks: ['prod_digital_checking'],
    tags: ['infrastructure', 'network', 'essential'],
    isHidden: false,
    isMandatory: true
  },

  // Security Objectives
  {
    id: 'inf_security_basics',
    title: 'Implement Basic Security',
    description: 'Establish fundamental security controls for your banking operations',
    detailedDescription: 'Basic security controls including firewalls and monitoring systems are essential for protecting customer data and preventing fraud. This foundation enables compliance with financial regulations and builds customer trust.',
    categoryId: 'security',
    subcategoryId: 'preventive_security',
    difficulty: 'easy',
    importance: 'critical',
    status: 'locked',
    progress: 0,
    maxValue: 1,
    criteria: {
      type: 'combination',
      value: 2,
      conditions: [
        {
          stat: 'Firewall',
          operator: '>=',
          value: 1,
          description: 'Deploy firewall protection'
        },
        {
          stat: 'compliance',
          operator: '>=',
          value: 60,
          description: 'Maintain 60% compliance'
        }
      ]
    },
    prerequisites: [
      {
        type: 'objective',
        id: 'inf_core_hub',
        description: 'Complete: Establish Banking Hub'
      }
    ],
    rewards: [
      {
        type: 'money',
        value: 1000,
        description: 'Security implementation bonus'
      },
      {
        type: 'building_unlock',
        value: 'Security',
        description: 'Unlocks advanced security buildings'
      }
    ],
    isRepeatable: false,
    hints: [
      'Firewalls prevent unauthorized access to your network',
      'Security is an ongoing process, not a one-time setup',
      'Consider future security needs when planning'
    ],
    successMetrics: {
      primary: 'Deploy firewall and maintain 60% compliance',
      secondary: ['No security breaches', 'Pass basic security audit']
    },
    dependencies: ['inf_core_hub'],
    unlocks: ['inf_security_advanced', 'compliance_basic'],
    tags: ['security', 'compliance', 'essential'],
    isHidden: false,
    isMandatory: true
  },

  {
    id: 'inf_security_aml',
    title: 'Deploy AML Unit',
    description: 'Implement Anti-Money Laundering detection and prevention systems',
    detailedDescription: 'AML (Anti-Money Laundering) systems are mandatory for financial institutions. These units monitor transactions for suspicious activities and help prevent financial crimes, ensuring regulatory compliance and protecting your institution from fines.',
    categoryId: 'security',
    subcategoryId: 'detective_security',
    difficulty: 'medium',
    importance: 'critical',
    status: 'locked',
    progress: 0,
    maxValue: 1,
    criteria: {
      type: 'count',
      value: 'SecurityAML',
      operator: '>='
    },
    prerequisites: [
      {
        type: 'objective',
        id: 'inf_security_basics',
        description: 'Complete: Implement Basic Security'
      }
    ],
    rewards: [
      {
        type: 'money',
        value: 1500,
        description: 'AML compliance bonus'
      },
      {
        type: 'stat_boost',
        value: 'compliance:10',
        description: '+10% Compliance Generation'
      }
    ],
    isRepeatable: false,
    timeLimit: 50,
    hints: [
      'AML units provide steady compliance generation',
      'Place AML units strategically for maximum coverage',
      'Consider transaction volume when planning AML capacity'
    ],
    successMetrics: {
      primary: 'Build 1 AML Unit',
      secondary: ['Achieve 70% compliance', 'Prevent money laundering incidents'],
      tertiary: ['Optimize AML efficiency']
    },
    dependencies: ['inf_security_basics'],
    unlocks: ['compliance_regulatory'],
    tags: ['security', 'compliance', 'aml', 'critical'],
    isHidden: false,
    isMandatory: true
  },

  // Compliance Objectives
  {
    id: 'compliance_basic',
    title: 'Achieve Basic Compliance',
    description: 'Reach 70% compliance to satisfy basic regulatory requirements',
    detailedDescription: 'Basic regulatory compliance demonstrates that your neobank meets essential financial services requirements. This milestone enables you to operate legally and builds foundation for advanced compliance.',
    categoryId: 'compliance',
    subcategoryId: 'basic_compliance',
    difficulty: 'medium',
    importance: 'high',
    status: 'locked',
    progress: 0,
    maxValue: 70,
    criteria: {
      type: 'threshold',
      value: 'compliance',
      operator: '>='
    },
    prerequisites: [
      {
        type: 'objective',
        id: 'inf_security_basics',
        description: 'Complete: Implement Basic Security'
      }
    ],
    rewards: [
      {
        type: 'money',
        value: 2000,
        description: 'Compliance achievement bonus'
      },
      {
        type: 'product_unlock',
        value: 'Digital Checking',
        description: 'Unlocks Digital Checking Account'
      }
    ],
    isRepeatable: false,
    hints: [
      'Combine security and compliance buildings',
      'Monitor compliance trends regularly',
      'Prevent compliance drops below 50% to avoid audits'
    ],
    successMetrics: {
      primary: 'Reach 70% compliance',
      secondary: ['Maintain for 5 ticks', 'No major violations']
    },
    failureConditions: [
      {
        stat: 'compliance',
        operator: '<',
        value: 40,
        description: 'Compliance drops below 40% (critical failure)'
      }
    ],
    dependencies: ['inf_security_basics'],
    unlocks: ['compliance_regulatory', 'prod_digital_checking'],
    tags: ['compliance', 'regulatory', 'milestone'],
    isHidden: false,
    isMandatory: true
  },

  {
    id: 'compliance_regulatory',
    title: 'Regulatory Certification',
    description: 'Achieve 80% compliance for full regulatory certification',
    detailedDescription: 'Full regulatory certification demonstrates comprehensive compliance with financial regulations. This achievement enables advanced product offerings and significantly increases customer trust and market opportunities.',
    categoryId: 'compliance',
    subcategoryId: 'certifications',
    difficulty: 'hard',
    importance: 'high',
    status: 'locked',
    progress: 0,
    maxValue: 80,
    criteria: {
      type: 'threshold',
      value: 'compliance',
      operator: '>='
    },
    prerequisites: [
      {
        type: 'objective',
        id: 'compliance_basic',
        description: 'Complete: Achieve Basic Compliance'
      },
      {
        type: 'objective',
        id: 'inf_security_aml',
        description: 'Complete: Deploy AML Unit'
      }
    ],
    rewards: [
      {
        type: 'money',
        value: 5000,
        description: 'Regulatory certification bonus'
      },
      {
        type: 'building_unlock',
        value: 'Compliance Advanced',
        description: 'Unlocks advanced compliance facilities'
      },
      {
        type: 'product_unlock',
        value: 'Premium Products',
        description: 'Unlocks premium financial products'
      }
    ],
    isRepeatable: false,
    timeLimit: 100,
    hints: [
      'Advanced compliance requires multiple compliance systems',
      'Consider SIEM and advanced monitoring',
      'Document all compliance procedures for audit readiness'
    ],
    successMetrics: {
      primary: 'Reach 80% compliance',
      secondary: ['Pass regulatory audit', 'Maintain for 10 ticks'],
      tertiary: ['Zero compliance violations']
    },
    completionBonus: {
      earlyCompletion: 25,
      perfectExecution: 50
    },
    dependencies: ['compliance_basic', 'inf_security_aml'],
    unlocks: ['compliance_advanced', 'prod_investment'],
    tags: ['compliance', 'certification', 'milestone', 'premium'],
    isHidden: false,
    isMandatory: false
  },

  // Product Objectives
  {
    id: 'prod_digital_checking',
    title: 'Launch Digital Checking',
    description: 'Introduce your first consumer banking product',
    detailedDescription: 'Digital checking accounts are the foundation of consumer banking. This product generates revenue through account fees and provides customers with essential financial services. Successful launch demonstrates your ability to deliver reliable financial products.',
    categoryId: 'products',
    subcategoryId: 'consumer_products',
    difficulty: 'medium',
    importance: 'high',
    status: 'locked',
    progress: 0,
    maxValue: 1,
    criteria: {
      type: 'count',
      value: 'Digital Checking',
      operator: '>='
    },
    prerequisites: [
      {
        type: 'objective',
        id: 'compliance_basic',
        description: 'Complete: Achieve Basic Compliance'
      },
      {
        type: 'objective',
        id: 'inf_connectivity_basic',
        description: 'Complete: Establish Network Connectivity'
      }
    ],
    rewards: [
      {
        type: 'money',
        value: 3000,
        description: 'Product launch bonus'
      },
      {
        type: 'stat_boost',
        value: 'budget:500',
        description: '+$500 recurring revenue'
      }
    ],
    isRepeatable: false,
    timeLimit: 75,
    hints: [
      'Digital checking requires 70% compliance',
      'Network connectivity is essential for processing',
      'Market readiness affects product success'
    ],
    successMetrics: {
      primary: 'Launch Digital Checking Account',
      secondary: ['Achieve positive revenue', 'Maintain compliance after launch']
    },
    dependencies: ['compliance_basic', 'inf_connectivity_basic'],
    unlocks: ['prod_savings', 'prod_credit_card'],
    tags: ['product', 'consumer', 'revenue', 'milestone'],
    isHidden: false,
    isMandatory: true
  },

  {
    id: 'prod_savings',
    title: 'Launch Savings Account',
    description: 'Introduce savings accounts to attract customer deposits',
    detailedDescription: 'Savings accounts help attract customer deposits and provide additional revenue streams through interest rate spreads. This product diversifies your offerings and increases customer retention.',
    categoryId: 'products',
    subcategoryId: 'consumer_products',
    difficulty: 'medium',
    importance: 'medium',
    status: 'locked',
    progress: 0,
    maxValue: 1,
    criteria: {
      type: 'combination',
      value: 2,
      conditions: [
        {
          stat: 'Savings Account',
          operator: '>=',
          value: 1,
          description: 'Launch savings product'
        },
        {
          stat: 'compliance',
          operator: '>=',
          value: 75,
          description: 'Maintain 75% compliance'
        }
      ]
    },
    prerequisites: [
      {
        type: 'objective',
        id: 'prod_digital_checking',
        description: 'Complete: Launch Digital Checking'
      }
    ],
    rewards: [
      {
        type: 'money',
        value: 2000,
        description: 'Product diversification bonus'
      },
      {
        type: 'stat_boost',
        value: 'budget:300',
        description: '+$300 recurring revenue'
      }
    ],
    isRepeatable: false,
    hints: [
      'Savings accounts require strong compliance',
      'Customer deposits improve financial stability',
      'Interest rates affect product attractiveness'
    ],
    successMetrics: {
      primary: 'Launch Savings Account with 75% compliance',
      secondary: ['Acquire customer deposits', 'Maintain product profitability']
    },
    dependencies: ['prod_digital_checking'],
    unlocks: ['prod_investment'],
    tags: ['product', 'consumer', 'deposits'],
    isHidden: false,
    isMandatory: false
  },

  // Business Growth Objectives
  {
    id: 'business_profitability',
    title: 'Achieve Profitability',
    description: 'Reach positive cash flow and sustainable operations',
    detailedDescription: 'Achieving profitability demonstrates that your neobank model is sustainable. This milestone shows that you can generate sufficient revenue to cover costs and invest in future growth.',
    categoryId: 'business',
    subcategoryId: 'financial_metrics',
    difficulty: 'hard',
    importance: 'high',
    status: 'locked',
    progress: 0,
    maxValue: 10000,
    criteria: {
      type: 'threshold',
      value: 'budget',
      operator: '>='
    },
    prerequisites: [
      {
        type: 'objective',
        id: 'prod_digital_checking',
        description: 'Complete: Launch Digital Checking'
      }
    ],
    rewards: [
      {
        type: 'money',
        value: 10000,
        description: 'Profitability achievement bonus'
      },
      {
        type: 'building_unlock',
        value: 'Advanced Operations',
        description: 'Unlocks advanced operational facilities'
      }
    ],
    isRepeatable: false,
    timeLimit: 150,
    hints: [
      'Diversify revenue streams through multiple products',
      'Control costs through efficient operations',
      'Monitor cash flow trends regularly'
    ],
    successMetrics: {
      primary: 'Reach $10,000 budget',
      secondary: ['Maintain positive cash flow', 'Keep compliance above 70%']
    },
    dependencies: ['prod_digital_checking'],
    unlocks: ['business_scaling', 'business_ipo_preparation'],
    tags: ['business', 'profitability', 'milestone', 'growth'],
    isHidden: false,
    isMandatory: false
  },

  // Advanced Objectives
  {
    id: 'compliance_advanced',
    title: 'Advanced Compliance Framework',
    description: 'Implement industry-leading compliance and risk management',
    detailedDescription: 'Advanced compliance framework demonstrates excellence in regulatory adherence and risk management. This achievement positions your neobank as a leader in compliance and enables premium services.',
    categoryId: 'compliance',
    subcategoryId: 'advanced_compliance',
    difficulty: 'expert',
    importance: 'medium',
    status: 'locked',
    progress: 0,
    maxValue: 90,
    criteria: {
      type: 'combination',
      value: 3,
      conditions: [
        {
          stat: 'compliance',
          operator: '>=',
          value: 90,
          description: 'Achieve 90% compliance'
        },
        {
          stat: 'SIEM',
          operator: '>=',
          value: 1,
          description: 'Deploy SIEM system'
        },
        {
          stat: 'SecurityIncidents',
          operator: '<=',
          value: 2,
          description: 'Maintain minimal security incidents'
        }
      ]
    },
    prerequisites: [
      {
        type: 'objective',
        id: 'compliance_regulatory',
        description: 'Complete: Regulatory Certification'
      }
    ],
    rewards: [
      {
        type: 'money',
        value: 15000,
        description: 'Compliance excellence bonus'
      },
      {
        type: 'special',
        value: 'compliance_leader',
        description: 'Achieve industry compliance leadership'
      }
    ],
    isRepeatable: false,
    timeLimit: 200,
    hints: [
      'Advanced compliance requires sophisticated monitoring',
      'SIEM systems provide comprehensive security monitoring',
      'Zero-tolerance policy for compliance violations'
    ],
    successMetrics: {
      primary: 'Achieve 90% compliance with SIEM and minimal incidents',
      secondary: ['Pass advanced audit', 'Maintain for 15 ticks']
    },
    completionBonus: {
      earlyCompletion: 50,
      perfectExecution: 100
    },
    dependencies: ['compliance_regulatory'],
    unlocks: ['business_premium_services', 'compliance_enterprise'],
    tags: ['compliance', 'advanced', 'expert', 'premium'],
    isHidden: false,
    isMandatory: false
  },

  {
    id: 'business_ipo_preparation',
    title: 'IPO Preparation',
    description: 'Prepare your neobank for public offering',
    detailedDescription: 'IPO preparation demonstrates that your neobank has achieved sufficient scale and stability to go public. This is the ultimate achievement for a fintech startup.',
    categoryId: 'business',
    subcategoryId: 'financial_metrics',
    difficulty: 'expert',
    importance: 'low',
    status: 'locked',
    progress: 0,
    maxValue: 2000000,
    criteria: {
      type: 'combination',
      value: 4,
      conditions: [
        {
          stat: 'budget',
          operator: '>=',
          value: 2000000,
          description: 'Reach $2M valuation'
        },
        {
          stat: 'compliance',
          operator: '>=',
          value: 85,
          description: 'Maintain 85% compliance'
        },
        {
          stat: 'products',
          operator: '>=',
          value: 5,
          description: 'Launch 5+ products'
        },
        {
          stat: 'threat',
          operator: '<=',
          value: 10,
          description: 'Keep threat level minimal'
        }
      ]
    },
    prerequisites: [
      {
        type: 'objective',
        id: 'business_profitability',
        description: 'Complete: Achieve Profitability'
      },
      {
        type: 'objective',
        id: 'compliance_advanced',
        description: 'Complete: Advanced Compliance Framework'
      }
    ],
    rewards: [
      {
        type: 'money',
        value: 100000,
        description: 'IPO success bonus'
      },
      {
        type: 'special',
        value: 'ipo_achievement',
        description: 'Successfully take your neobank public'
      }
    ],
    isRepeatable: false,
    timeLimit: 365,
    hints: [
      'IPO requires exceptional financial performance',
      'Diversified product portfolio strengthens your position',
      'Impeccable compliance record is essential'
    ],
    successMetrics: {
      primary: 'Reach $2M valuation with 85% compliance and 5+ products',
      secondary: ['Maintain minimal threat level', 'Pass due diligence']
    },
    completionBonus: {
      earlyCompletion: 100,
      perfectExecution: 200
    },
    dependencies: ['business_profitability', 'compliance_advanced'],
    unlocks: [],
    tags: ['business', 'ipo', 'expert', 'endgame', 'achievement'],
    isHidden: false,
    isMandatory: false
  }
];

export const OBJECTIVE_SETS = [
  {
    id: 'startup_foundation',
    name: 'Startup Foundation',
    description: 'Essential objectives for establishing your neobank',
    categoryIds: ['infrastructure', 'security', 'compliance'],
    objectives: [
      'inf_core_hub',
      'inf_connectivity_basic',
      'inf_security_basics',
      'compliance_basic'
    ],
    requiredCompletions: 4,
    rewards: [
      {
        type: 'money',
        value: 5000,
        description: 'Startup foundation bonus'
      },
      {
        type: 'building_unlock',
        value: 'Tier 2 Buildings',
        description: 'Unlocks tier 2 infrastructure'
      }
    ],
    isSequential: true,
    timeLimit: 100
  },
  {
    id: 'product_launch',
    name: 'Product Launch Pipeline',
    description: 'Launch your core financial products',
    categoryIds: ['products', 'compliance'],
    objectives: [
      'prod_digital_checking',
      'prod_savings'
    ],
    requiredCompletions: 2,
    rewards: [
      {
        type: 'money',
        value: 8000,
        description: 'Product launch success bonus'
      },
      {
        type: 'product_unlock',
        value: 'Premium Products',
        description: 'Unlocks premium product categories'
      }
    ],
    isSequential: false,
    timeLimit: 150
  },
  {
    id: 'growth_trajectory',
    name: 'Growth Trajectory',
    description: 'Scale your neobank to profitability',
    categoryIds: ['business', 'compliance', 'products'],
    objectives: [
      'business_profitability',
      'compliance_regulatory'
    ],
    requiredCompletions: 2,
    rewards: [
      {
        type: 'money',
        value: 20000,
        description: 'Growth milestone bonus'
      },
      {
        type: 'special',
        value: 'market_leader',
        description: 'Achieve market leadership position'
      }
    ],
    isSequential: false,
    timeLimit: 250
  }
];